import { api, ApiError, URLs } from '@/libs/api'
import type {
  ApiResponse,
  WeeklyNotePayload,
  WeeklyNotesData,
  WeeklyNotesParams,
} from '@/libs/interfaces'
import { IS_MOCK_MODE } from '@/libs/mocks/config'
import { weeklyNotesApiMock } from '@/libs/mocks/handlers/weeklyNotes'

// The backend returns 404 when no week note exists yet for a store+week — that's the
// normal state for most weeks, not an error. Treat it the same way the mock does:
// synthesize an empty note instead of throwing, so this doesn't trip the global
// query-error redirect (see .claude/memory/global-loading-and-error-handling.md).
const emptyWeeklyNotes = (params: WeeklyNotesParams): WeeklyNotesData => ({
  weekNote: {
    weekNoteID: '',
    storeID: params.storeID ?? '',
    week: params.week ?? '',
    note: '',
    createdAt: '',
    updatedAt: '',
  },
  dailyNotes: [],
})

const weeklyNotesApiReal = {
  getWeeklyNotes: async (params: WeeklyNotesParams): Promise<WeeklyNotesData> => {
    try {
      const response = await api.get<ApiResponse<WeeklyNotesData>>(
        URLs.WeeklyNotes.getWeeklyNotes(params?.storeID || '', params?.week || ''),
      )
      return response.data?.data
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return emptyWeeklyNotes(params)
      }
      throw error
    }
  },

  createOrUpdateWeeklyNotes: async (payload: WeeklyNotePayload): Promise<WeeklyNotesData> =>
    await api
      .put<ApiResponse<WeeklyNotesData>>(URLs.WeeklyNotes.put(payload.storeID), {
        week: payload.week,
        weekNote: payload.weekNote,
        dailyNotes: payload.dailyNotes,
      })
      .then((response) => response.data.data),
}

export const weeklyNotesApi: typeof weeklyNotesApiReal = IS_MOCK_MODE
  ? weeklyNotesApiMock
  : weeklyNotesApiReal
